<script lang="ts">

  import httpClient from "$lib/axios-client"
  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import type { _response } from "$lib/data/type/_response"
  import { sleep } from "$lib/sleep"
  import EyeIcon from "@lucide/svelte/icons/eye"
  import EyeOffIcon from "@lucide/svelte/icons/eye-off"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"

  const FormSchema = z.object({
    password: z.string().default(""),
    password_show: z.boolean().default(false),
    password_new: z.string().default(""),
    password_new_show: z.boolean().default(false),
    password_new_confirm: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  let { open, setOpen }: {
    open: boolean
    setOpen: (open: boolean) => void
  } = $props()

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))

  createQuery(() => ({
    enabled: open,
    queryKey: ["user/password-change"],
    queryFn: async () => {

      await sleep(1)
      form.reset(FormEmpty)

      return ""
    }
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const response = await httpClient.patch<_response<string>>("/api/user/password", data)
      const responseData = response.data!

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (prev) => ({
          ...prev,
          errorMap: { onServer: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) {
        return
      }

      form.reset(FormEmpty)
      toast.success(responseData.message)

      setOpen(false)
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message
      })
    }
  }))

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}
>
  <DialogContent
    class="relative w-sm"
    onOpenAutoFocus={(e) => e.preventDefault()}
    onInteractOutside={(e) => e.preventDefault()}
  >
    <DialogHeader>
      <DialogTitle>Ubah Password</DialogTitle>
      <DialogDescription>
        Untuk menjaga keamanan, silahkan ubah password secara berkala
      </DialogDescription>
    </DialogHeader>
    <Loader show={mutation.isPending}/>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-6"
      onsubmit={(e) => {
        e.preventDefault()
        mutation.mutate(form.state.values)
      }}
    >
      <div class="space-y-3">
        <form.Field name="password">
          {#snippet children(field)}
            {@const show = form.useSelector((state) => state.values.password_show)}
            <div>
              <FieldLabel>Kata Sandi</FieldLabel>
              <div class="relative">
                <Input
                  type={show.current ? "text" : "password"}
                  name={field.name}
                  class="pe-9"
                  autocapitalize="off"
                  autocomplete="current-password"
                  value={field.state.value}
                  placeholder="••••••••"
                  oninput={(e) => field.handleChange(e.currentTarget.value)}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  class="absolute top-0.5 inset-e-0.5 group"
                  data-show={show.current}
                  onclick={() => {
                    form.setFieldValue("password_show", !form.getFieldValue("password_show"))
                  }}
                >
                  <EyeOffIcon class="size-4 group-data-[show=true]:block hidden"/>
                  <EyeIcon class="size-4 group-data-[show=false]:block hidden"/>
                </Button>
              </div>
              <FieldMessage name={field.name} {form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="password_new">
          {#snippet children(field)}
            {@const show = form.useSelector((state) => state.values.password_new_show)}
            <div>
              <FieldLabel>Kata Sandi Baru</FieldLabel>
              <div class="relative">
                <Input
                  type={show.current ? "text" : "password"}
                  name={field.name}
                  class="pe-9"
                  autocapitalize="off"
                  autocomplete="new-password"
                  value={field.state.value}
                  placeholder="••••••••"
                  oninput={(e) => field.handleChange(e.currentTarget.value)}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  class="absolute top-0.5 inset-e-0.5 group"
                  data-show={show.current}
                  onclick={() => {
                    form.setFieldValue("password_new_show", !form.getFieldValue("password_new_show"))
                  }}
                >
                  <EyeOffIcon class="size-4 group-data-[show=true]:block hidden"/>
                  <EyeIcon class="size-4 group-data-[show=false]:block hidden"/>
                </Button>
              </div>
              <FieldMessage name={field.name} {form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="password_new_confirm">
          {#snippet children(field)}
            <div>
              <FieldLabel>Kata Sandi Konfirmasi</FieldLabel>
              <Input
                type="password"
                name={field.name}
                autocomplete="new-password"
                placeholder="••••••••"
                value={field.state.value}
                oninput={(e) => field.handleChange(e.currentTarget.value)}
              />
              <FieldMessage name={field.name} {form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <DialogFooter>
        <div class="flex-1"></div>
        <Button
          variant="outline"
          disabled={mutation.isPending}
          onclick={() => setOpen(false)}
        >
          Batal
        </Button>
        <Button
          type="submit"
          disabled={mutation.isPending}
        >
          Ubah
        </Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
